import React from "react";
import { Download, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { downloadPdf } from "@/components/shownotes/download";
import { fmtTime } from "@/components/PageHeader";
import { formatDate, formatDateOnly } from "@/lib/date";
import { KIND_CHIP, KIND_LABEL, DEPTS, TIME_ROWS, fmtMinutes, noteTitle } from "@/components/shownotes/meta";
import NoteSection from "@/components/shownotes/NoteSection";
import SceneRunView from "@/components/shownotes/SceneRunView";

const Item = ({ label, children, testId }) => (
  <div><dt className="text-xs font-semibold text-slate-500">{label}</dt><dd className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800" data-testid={testId}>{children || "—"}</dd></div>
);

function Times({ times }) {
  return (
    <div className="grid grid-cols-3 gap-y-1.5 text-sm" data-testid="note-view-times">
      <span /><span className="text-xs font-semibold text-slate-500">Time</span><span className="text-xs font-semibold text-slate-500">Duration</span>
      {[...TIME_ROWS, ["total", "Total Time"]].map(([k, label]) => (
        <React.Fragment key={k}>
          <span className={k === "total" ? "font-bold text-slate-900" : "text-slate-700"}>{label}</span>
          <span className="tabular-nums">{fmtTime(times?.[k]?.time) || "—"}</span>
          <span className={`tabular-nums ${k === "total" ? "font-bold" : ""}`} data-testid={`note-view-duration-${k}`}>{fmtMinutes(times?.[k]?.duration)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// Read-only published (or submitted) notes; directors can download published ones as PDF, and edit or delete them.
export default function NoteView({ note, onClose, canDownload, onEdit, onDelete }) {
  const f = note?.fields || {};
  const published = note?.status === "published";
  return (
    <Dialog open={!!note} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto bg-slate-50" data-testid="note-view-dialog">
        {note && <>
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3 pr-6">
              <span className={`w-fit rounded-full border px-2.5 py-0.5 text-xs font-bold ${KIND_CHIP[note.kind]}`}>{KIND_LABEL[note.kind]}</span>
              <div className="flex flex-wrap items-center gap-2">
                {published && onEdit && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(note)} data-testid="note-view-edit">
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                )}
                {published && onDelete && (
                  <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => onDelete(note)} data-testid="note-view-delete">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                  </Button>
                )}
                {canDownload && published && (
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="note-view-download-pdf"
                    onClick={() => downloadPdf(`/show-notes/${note.id}/pdf`, {}, "show-notes.pdf")}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> Download PDF
                  </Button>
                )}
              </div>
            </div>
            <DialogTitle className="font-heading text-xl" data-testid="note-view-title">{noteTitle(note)}</DialogTitle>
            <DialogDescription>{formatDateOnly(f.date)}{note.reviewer_name ? ` · Approved by ${note.reviewer_name}` : ""}{note.edited_by_name ? ` · Edited by ${note.edited_by_name}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <NoteSection title="Report Details">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Item label="Submitted by">{note.author_name} on {formatDate(note.submitted_at)}</Item>
                <Item label="Report Number">{note.report_number ? `#${note.report_number}` : "Assigned on approval"}</Item>
                <Item label="Date">{formatDateOnly(f.date)}</Item>
                <Item label="Manager" testId="note-view-managers">{(note.managers || []).join(", ")}</Item>
                {note.kind === "rehearsal" ? <Item label="Space">{f.space}</Item> : <>
                  <Item label="House Count">{f.house_count}</Item>
                  <Item label="Standing Ovation">{f.standing_ovation === "yes" ? "Yes" : f.standing_ovation === "no" ? "No" : ""}</Item>
                  <Item label="Audience Reaction">{f.audience_reaction}</Item>
                </>}
              </dl>
            </NoteSection>
            {note.kind === "rehearsal" ? (
              <NoteSection title="Rehearsal">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Item label="Material Rehearsed">{f.material}</Item></div>
                  <div className="sm:col-span-2"><dt className="mb-1 text-xs font-semibold text-slate-500">Scene Run</dt><SceneRunView scenes={f.scenes} /></div>
                  <Item label="Breaks">{(f.breaks || []).map(fmtTime).join(", ")}</Item>
                </dl>
              </NoteSection>
            ) : <NoteSection title="Times"><Times times={f.times} /></NoteSection>}
            <NoteSection title="Department Notes">
              <dl className="grid gap-4 sm:grid-cols-2">
                {DEPTS.map(([k, label]) => <Item key={k} label={label} testId={`note-view-dept-${k}`}>{f.dept?.[k] || <span className="text-slate-400">No notes</span>}</Item>)}
              </dl>
            </NoteSection>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
